import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Calculator, Globe2, LayoutDashboard, LogOut, Package, PackagePlus, PackageX, ShoppingBag, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Brand } from "../../components/Navbar";
import ChangePassword from "../../components/ChangePassword";
import api from "../../lib/api";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "cust-nav-dashboard" },
  { to: "/app/book", label: "Book Domestic", icon: PackagePlus, testid: "cust-nav-book-domestic" },
  { to: "/app/book-international", label: "Book International", icon: Globe2, testid: "cust-nav-book-international" },
  { to: "/app/shipments", label: "Shipments", icon: Package, testid: "cust-nav-shipments" },
  { to: "/app/ndr", label: "NDR / RTO", icon: PackageX, testid: "cust-nav-ndr", partners: true },
  { to: "/app/orders", label: "My Orders", icon: ShoppingBag, testid: "cust-nav-orders" },
  { to: "/app/rates", label: "Rate Calculator", icon: Calculator, testid: "cust-nav-rates" },
  { to: "/app/wallet", label: "Wallet", icon: Wallet, testid: "cust-nav-wallet" },
];

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  useEffect(() => {
    api.get("/public/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
  }, []);
  const nav = NAV.filter((n) => !n.partners || ["agent", "franchise", "branch_manager"].includes(user?.role));
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-header fixed inset-x-0 top-0 z-40">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Brand />
          <div className="flex items-center gap-4">
            <span data-testid="cust-user-name" className="hidden text-sm font-semibold text-navy sm:block">{user?.name}</span>
            <span className="rounded-full bg-saffron-light px-3 py-1 text-xs font-bold capitalize text-saffron">{user?.role}</span>
            <ChangePassword testid="cust-change-password-btn" iconOnly
                            triggerClass="rounded-full p-2 text-slate-500 hover:bg-slate-100" />
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="cust-logout-btn"
                    className="rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        <nav className="border-t border-slate-200/70 bg-white/60 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 sm:px-6">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                       className={({ isActive }) =>
                         `flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                           isActive ? "border-saffron text-saffron" : "border-transparent text-slate-500 hover:text-navy"}`}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
        {announcements.length > 0 && (
          <div className="border-t border-slate-200/70 bg-gradient-to-r from-saffron via-[#ff7a3d] to-express text-white" data-testid="announcement-strip">
            <div className="mx-auto flex max-w-7xl items-center gap-6 overflow-x-auto px-4 py-2 sm:px-6">
              {announcements.map((a) => (
                <a key={a.id} href={a.link || undefined} data-testid={`announcement-${a.id}`}
                   className="flex shrink-0 items-center gap-2.5 text-xs font-semibold transition-opacity hover:opacity-80">
                  {a.image_url && <img src={a.image_url} alt="" className="h-7 w-7 rounded-md object-cover ring-1 ring-white/40" />}
                  <span className="animate-beam rounded bg-white/25 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider">New</span>
                  <span>{a.title ? `${a.title} — ` : ""}{a.text}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </header>
      <main className={`mx-auto max-w-7xl px-4 pb-16 sm:px-6 ${announcements.length ? "pt-44" : "pt-36"}`}>
        <Outlet />
      </main>
    </div>
  );
}
